import { isValidPhoneNumber } from 'libphonenumber-js/max'
import { z, ZodString } from 'zod'

import {
    CompanyInnErrorKey,
    IndividualInnErrorKey,
    validateCompanyInn,
    validateIndividualInn,
} from './juridical'

export const zEmptyString = () => z.string().trim().max(0)

export const zodIndividualInn = (
    zodString: ZodString,
    errorMap: Record<IndividualInnErrorKey, string>
) => {
    return zodString
        .length(12, {
            abort: true,
            error: errorMap.invalid_length_12,
        })
        .check((ctx) => {
            const result = validateIndividualInn(ctx.value)
            if (!result.valid) {
                ctx.issues.push({
                    code: 'custom',
                    input: ctx.value,
                    continue: false,
                    message: errorMap[result.error],
                })
            }
        })
}

export const zodCompanyInn = (
    zodString: ZodString,
    errorMap: Record<CompanyInnErrorKey, string>
) => {
    return zodString
        .length(10, {
            abort: true,
            error: errorMap.invalid_length_10,
        })
        .check((ctx) => {
            const result = validateCompanyInn(ctx.value)
            if (!result.valid) {
                ctx.issues.push({
                    code: 'custom',
                    input: ctx.value,
                    continue: false,
                    message: errorMap[result.error],
                })
            }
        })
}

export type UniversalInnErrorKey =
    | 'invalid_characters'
    | 'invalid_length'
    | 'invalid_checksum'

export const zodUniversalInn = (
    zodString: ZodString,
    errorMap: Record<UniversalInnErrorKey, string>
) => {
    return zodString
        .regex(/^\d+$/, {
            abort: true,
            error: errorMap.invalid_characters,
        })
        .check((ctx) => {
            const value = ctx.value

            if (value.length !== 10 && value.length !== 12) {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    continue: false,
                    message: errorMap.invalid_length,
                })
                return
            }

            if (value.length === 10) {
                const result = validateCompanyInn(value)
                if (!result.valid) {
                    ctx.issues.push({
                        code: 'custom',
                        input: value,
                        continue: false,
                        message: errorMap.invalid_checksum,
                    })
                }
            } else {
                const result = validateIndividualInn(value)
                if (!result.valid) {
                    ctx.issues.push({
                        code: 'custom',
                        input: value,
                        continue: false,
                        message: errorMap.invalid_checksum,
                    })
                }
            }
        })
}

export const zodBic = (
    zodString: ZodString,
    abort: boolean,
    errorMap: Record<'invalid_length_9' | 'invalid_characters', string>
) => {
    return zodString
        .length(9, { abort, error: errorMap.invalid_length_9 })
        .regex(/^\d+$/, { abort, error: errorMap.invalid_characters })
}
export const zodBankAccount = (
    zodString: ZodString,
    errorMap: Record<'invalid_length_20' | 'invalid_characters', string>,
    allowEmpty = false
) => {
    if (allowEmpty) {
        return zodString.superRefine((val, ctx) => {
            if (val.length === 0) return

            if (!/^\d+$/.test(val)) {
                ctx.addIssue({
                    code: 'custom',
                    message: errorMap.invalid_characters,
                })
                return
            }

            if (val.length !== 20) {
                ctx.addIssue({
                    code: 'custom',
                    message: errorMap.invalid_length_20,
                })
            }
        })
    }

    return zodString
        .regex(/^\d+$/, {
            abort: true,
            error: errorMap.invalid_characters,
        })
        .length(20, {
            abort: true,
            error: errorMap.invalid_length_20,
        })
}

export const zVat = <T = string>(
    errorMap: Record<'number' | 'gte_-1' | 'lte_22', string>
) =>
    z.coerce
        .number<T>(errorMap.number)
        .gte(-1, errorMap['gte_-1'])
        .lte(22, errorMap.lte_22)

export const zodPhone = (_message: string) =>
    z
        .string()
        // .regex(/^\+\d[0-9\s()-]{7,15}$/, { error: _message })
        .refine((val) => isValidPhoneNumber(val), {
            error: 'Некорректный номер телефона',
        })

// Паспорт РФ: 4 цифры серии + пробел + 6 цифр номера (например, "1234 567890")
export const zodPassport = (message: string) =>
    z.string().regex(/^\d{4} \d{6}$/, {
        message,
    })
